import { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { RecordOpen } from '../tasks/model.ts';
import { formatDate } from '../dates.ts';
import { asSentence } from '../sentence.ts';
import { OpenedRecord } from './OpenedRecord.tsx';
import { Refusal } from './PropertiesPanel.tsx';
import { ReasonFields, useReasonReady } from './VisitDetails.tsx';
import { RecordEdit } from './RecordEdit.ts';
import { MoneyBox } from './MoneyBox.tsx';
import { formatMoneyInput, moneyText, parseMoneyInput } from '../moneyInput.ts';
import { formatMoneyText } from '../money.ts';
import {
  bondState, procuredSaid, useClientBonds, useCorrectBond, useRecordBond, useWithdrawBond,
  type Bond, type BondEvidence, type BondOrder, type BondInput,
} from '../api/bonds.ts';

/**
 * The client's bonds, recorded against their orders (162, P-35; Arthur, 29 October 2026), in the Legal section. The
 * BOND is the one source: recording an issued bond completes its order's "Procure the bond" deadline on the issued
 * day, a correction moves it, a withdrawal reopens only what a bond met. Who records: Legal and tiers 1-2 (the
 * server's may_record); who corrects and withdraws: whoever may record a court order (may_edit). The A: drive's bond
 * documents are shown as their filenames read them, read only; an undecided one says so and closes nothing.
 */

const field = 'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';
const hint = 'mt-1 text-sm text-neutral-600';
const primary = 'min-h-[44px] rounded-lg bg-neutral-900 px-4 text-sm font-semibold text-white disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed';
const secondary = 'min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold text-neutral-800 hover:bg-neutral-50';

function todayLocal(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date());
}

const orderWords = (o: { order_type: string | null; signed_date: string | null }) =>
  `${o.order_type ?? 'Order'}${o.signed_date ? ` of ${formatDate(o.signed_date)}` : ', date not known'}`;

const WHOSE: Record<BondEvidence['whose'], string> = {
  agency: 'the agency’s bond', 'predecessor guardian': 'a predecessor guardian’s bond (never the agency’s)',
  undecided: 'whose bond is undecided (not linked; closes nothing)',
};

export function BondsPanel({ clientId }: { clientId: string }) {
  const q = useClientBonds(clientId);
  const asked = useLocation().state as RecordOpen | null;
  const showOrder = asked?.show_order ?? null;
  const [said, setSaid] = useState<string | null>(null);
  if (q.error && !q.data) return <div className="px-4 pb-3"><Refusal error={q.error} /></div>;
  if (!q.data) return <p className="px-4 pb-3 text-neutral-600">Loading the bonds…</p>;
  const { bonds, orders, evidence, may_record, may_edit } = q.data;
  const untied = bonds.filter((b) => b.court_order_id === null);
  if (orders.length === 0 && bonds.length === 0 && evidence.length === 0) {
    return <p className="px-4 pb-3 text-neutral-600">No order on file requires a bond, and none is recorded.</p>;
  }
  return (
    <div>
      {said && <p role="status" className="px-4 pt-2 text-sm text-neutral-800">{said}</p>}
      <ul className="divide-y divide-neutral-100 border-t border-neutral-200">
        {orders.map((o) => (
          <OpenedRecord key={o.id} id={`bond-order-${o.id}`} opened={o.id === showOrder} from={asked?.opened_from} className="px-4 py-3">
            <OrderBonds clientId={clientId} order={o} bonds={bonds.filter((b) => b.court_order_id === o.id)}
                        mayRecord={may_record} mayEdit={may_edit} onSaid={setSaid} />
          </OpenedRecord>
        ))}
      </ul>
      {untied.length > 0 && (
        <section aria-labelledby={`untied-${clientId}`} className="border-t border-neutral-200 px-4 py-3">
          <h3 id={`untied-${clientId}`} className="font-semibold text-neutral-900">Bonds not yet tied to an order</h3>
          <p className={hint}>A bond tied to its order completes that order&rsquo;s deadline; until then it completes nothing.</p>
          <ul className="mt-2 space-y-2">
            {untied.map((b) => (
              <li key={b.id}>
                <BondRow clientId={clientId} bond={b} orders={orders} mayEdit={may_edit} onSaid={setSaid} />
              </li>
            ))}
          </ul>
        </section>
      )}
      {evidence.length > 0 && (
        <section aria-labelledby={`evidence-${clientId}`} className="border-t border-neutral-200 px-4 py-3">
          <h3 id={`evidence-${clientId}`} className="font-semibold text-neutral-900">The bond documents on file</h3>
          <p className={hint}>As each document&rsquo;s file name reads, read only. They never change a bond above.</p>
          <ul className="mt-2 space-y-1 text-sm text-neutral-800">
            {evidence.map((e) => (
              <li key={e.id}>
                {e.kind}{e.date_in_filename ? `, ${formatDate(e.date_in_filename)}` : ', no date in the name'}
                {e.amount_in_filename ? `, $${e.amount_in_filename}` : ''} · {WHOSE[e.whose]}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function OrderBonds({ clientId, order, bonds, mayRecord, mayEdit, onSaid }: {
  clientId: string; order: BondOrder; bonds: Bond[]; mayRecord: boolean; mayEdit: boolean; onSaid: (m: string) => void;
}) {
  const [recording, setRecording] = useState(false);
  return (
    <>
      <p className="font-semibold text-neutral-900">{orderWords(order)}</p>
      <p className="text-sm text-neutral-700">
        {order.bond_required === null ? 'The order does not say whether a bond is required; recording one says it was. ' : ''}
        {procuredSaid(order, formatDate)}
      </p>
      {bonds.length > 0 && (
        <ul className="mt-2 space-y-2">
          {bonds.map((b) => <li key={b.id}><BondRow clientId={clientId} bond={b} orders={[order]} mayEdit={mayEdit} onSaid={onSaid} /></li>)}
        </ul>
      )}
      {mayRecord && !recording && (
        <button type="button" className={`${secondary} mt-2`} onClick={() => setRecording(true)}>Record the bond</button>
      )}
      {recording && (
        <BondForm clientId={clientId} orderId={order.id} riders={bonds.filter((b) => !b.withdrawn && !b.increases_bond_id)}
                  signedDate={order.signed_date}
                  onDone={(m) => { setRecording(false); if (m) onSaid(m); }} />
      )}
    </>
  );
}

function BondRow({ clientId, bond, orders, mayEdit, onSaid }: {
  clientId: string; bond: Bond; orders: BondOrder[]; mayEdit: boolean; onSaid: (m: string) => void;
}) {
  const [mode, setMode] = useState<'none' | 'correct' | 'withdraw'>('none');
  return (
    <div className="rounded-lg border border-neutral-200 px-3 py-2">
      <p className="text-neutral-900">
        <span className="font-semibold">{bond.increases_bond_id ? 'Rider' : 'Bond'}</span>
        {bond.amount ? ` · ${formatMoneyText(bond.amount)}` : ''} · {bondState(bond, formatDate)}
        {bond.surety_name ? ` · ${bond.surety_name}` : ''}{bond.bond_number ? ` · No. ${bond.bond_number}` : ''}
        {bond.versions > 1 && <span className="text-sm text-neutral-600"> · edited</span>}
      </p>
      {bond.withdrawn && <p className="text-sm text-neutral-700">Withdrawn: {asSentence(bond.withdrawal_reason)} It counts for nothing.</p>}
      {mode === 'none' && (
        <div className="mt-1">
          {/* the one Edit menu every record uses (RecordEdit): a correction with a reason, or a withdrawal */}
          <RecordEdit may={mayEdit && !bond.withdrawn} className={secondary} choices={[
            { key: 'correct', label: bond.court_order_id ? 'Correct' : 'Tie it to its order, or correct', onPick: () => setMode('correct') },
            { key: 'withdraw', label: 'Withdraw: recorded in error', onPick: () => setMode('withdraw') },
          ]} />
        </div>
      )}
      {mode === 'correct' && (
        <CorrectBond clientId={clientId} bond={bond} orders={orders}
                     onDone={(m) => { setMode('none'); if (m) onSaid(m); }} />
      )}
      {mode === 'withdraw' && (
        <WithdrawBond clientId={clientId} bond={bond} onDone={(m) => { setMode('none'); if (m) onSaid(m); }} />
      )}
    </div>
  );
}

/** The bond's own form: the amount, the dates, the surety and the number. */
export function BondForm({ clientId, orderId, riders, signedDate, onDone }: {
  clientId: string; orderId: string; riders: Bond[]; signedDate: string | null; onDone: (m: string | null) => void;
}) {
  const [f, setF] = useState({ amount: '', applied_on: '', granted_on: '', unknown: false, surety: '', number: '', rider: '' });
  const record = useRecordBond(clientId);
  const sending = useRef(false);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF((x) => ({ ...x, [k]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value }));
  const id = `bond-${orderId}`;
  // F-100: an amount that is not money holds the form; an empty one is sent as none, as before
  const amountBad = parseMoneyInput(f.amount).kind === 'bad';
  return (
    <form className="mt-2 space-y-3 rounded-lg border border-neutral-300 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (sending.current || amountBad) return;
            sending.current = true;
            const body: BondInput & { orderId: string } = {
              orderId, amount: moneyText(f.amount), applied_on: f.applied_on || null,
              granted_on: f.unknown ? null : f.granted_on || null, granted_date_unknown: f.unknown,
              surety_name: f.surety.trim() || null, bond_number: f.number.trim() || null, increases_bond_id: f.rider || null,
            };
            record.mutate(body, {
              onSuccess: () => onDone(body.granted_on || body.granted_date_unknown
                ? 'Bond recorded. The order’s "Procure the bond" deadline is complete.'
                : 'Bond recorded. It is not yet issued, so the deadline stays open.'),
              onError: () => { sending.current = false; },
            });
          }}>
      <h3 className="font-semibold text-neutral-900">Record the bond</h3>
      <p className={hint}>The day the surety issued the bond completes this order&rsquo;s deadline; applied for alone completes nothing.</p>
      <div>
        <label className={label} htmlFor={`${id}-amount`}>Amount</label>
        <MoneyBox id={`${id}-amount`} className={field} value={f.amount} onChange={(v) => setF((x) => ({ ...x, amount: v }))} />
      </div>
      <div>
        <label className={label} htmlFor={`${id}-applied`}>Date applied for</label>
        <input id={`${id}-applied`} type="date" className={field} value={f.applied_on} max={todayLocal()} onChange={set('applied_on')} />
      </div>
      <div>
        <label className={label} htmlFor={`${id}-granted`}>Date the surety issued it</label>
        <input id={`${id}-granted`} type="date" className={field} value={f.granted_on} disabled={f.unknown}
               min={signedDate ?? undefined} max={todayLocal()} onChange={set('granted_on')} />
        <label className="mt-2 flex min-h-[44px] cursor-pointer items-start gap-3">
          <input type="checkbox" className="mt-1 size-5 shrink-0" checked={f.unknown} onChange={set('unknown')} />
          <span className="text-neutral-900">Issued, and the date is not known</span>
        </label>
      </div>
      <div>
        <label className={label} htmlFor={`${id}-surety`}>Surety</label>
        <input id={`${id}-surety`} className={field} value={f.surety} maxLength={200} onChange={set('surety')} />
      </div>
      <div>
        <label className={label} htmlFor={`${id}-number`}>Bond number</label>
        <input id={`${id}-number`} className={field} value={f.number} maxLength={60} onChange={set('number')} />
      </div>
      {riders.length > 0 && (
        <div>
          <label className={label} htmlFor={`${id}-rider`}>A rider on</label>
          <select id={`${id}-rider`} className={field} value={f.rider} onChange={set('rider')}>
            <option value="">No: a bond of its own</option>
            {riders.map((r) => <option key={r.id} value={r.id}>{r.amount ? `$${r.amount}, ` : ''}{bondState(r, formatDate)}</option>)}
          </select>
          <p className={hint}>A rider raises the bond it names; it procures nothing by itself.</p>
        </div>
      )}
      {record.error && <Refusal error={record.error} />}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={record.isPending || amountBad}>{record.isPending ? 'Recording…' : 'Record'}</button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}

function CorrectBond({ clientId, bond, orders, onDone }: {
  clientId: string; bond: Bond; orders: BondOrder[]; onDone: (m: string | null) => void;
}) {
  const [f, setF] = useState({
    amount: formatMoneyInput(bond.amount ?? ''), applied_on: bond.applied_on ?? '', granted_on: bond.granted_on ?? '',
    unknown: bond.granted_date_unknown, surety: bond.surety_name ?? '', number: bond.bond_number ?? '',
    order: bond.court_order_id ?? '',
  });
  const [reason, setReason] = useState('');
  const [text, setText] = useState('');
  const amountBad = parseMoneyInput(f.amount).kind === 'bad';
  const ready = useReasonReady(reason, text) && !amountBad;
  const correct = useCorrectBond(clientId);
  const id = `bondc-${bond.id}`;
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF((x) => ({ ...x, [k]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value }));
  return (
    <form className="mt-2 space-y-3 rounded-lg border border-neutral-300 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!ready) return;
            // a whole version: every field the bond holds, the changed ones replaced
            const fields = {
              ...bond.fields, amount: moneyText(f.amount), applied_on: f.applied_on || null,
              granted_on: f.unknown ? null : f.granted_on || null, granted_date_unknown: f.unknown,
              surety_name: f.surety.trim() || null, bond_number: f.number.trim() || null, court_order_id: f.order || null,
            };
            correct.mutate({ bondId: bond.id, fields, reason, ...(text.trim() ? { reason_text: text.trim() } : {}) },
              { onSuccess: () => onDone('Bond corrected. Its order’s deadline follows it.') });
          }}>
      <h3 className="font-semibold text-neutral-900">Correct the bond</h3>
      {orders.length > 0 && (
        <div>
          <label className={label} htmlFor={`${id}-order`}>Its order</label>
          <select id={`${id}-order`} className={field} value={f.order} onChange={set('order')}>
            <option value="">Not tied to an order</option>
            {orders.map((o) => <option key={o.id} value={o.id}>{orderWords(o)}</option>)}
          </select>
        </div>
      )}
      <div>
        <label className={label} htmlFor={`${id}-amount`}>Amount</label>
        <MoneyBox id={`${id}-amount`} className={field} value={f.amount} onChange={(v) => setF((x) => ({ ...x, amount: v }))} />
      </div>
      <div>
        <label className={label} htmlFor={`${id}-applied`}>Date applied for</label>
        <input id={`${id}-applied`} type="date" className={field} value={f.applied_on} max={todayLocal()} onChange={set('applied_on')} />
      </div>
      <div>
        <label className={label} htmlFor={`${id}-granted`}>Date the surety issued it</label>
        <input id={`${id}-granted`} type="date" className={field} value={f.granted_on} disabled={f.unknown} max={todayLocal()}
               onChange={set('granted_on')} />
        <label className="mt-2 flex min-h-[44px] cursor-pointer items-start gap-3">
          <input type="checkbox" className="mt-1 size-5 shrink-0" checked={f.unknown} onChange={set('unknown')} />
          <span className="text-neutral-900">Issued, and the date is not known</span>
        </label>
      </div>
      <div>
        <label className={label} htmlFor={`${id}-surety`}>Surety</label>
        <input id={`${id}-surety`} className={field} value={f.surety} maxLength={200} onChange={set('surety')} />
      </div>
      <div>
        <label className={label} htmlFor={`${id}-number`}>Bond number</label>
        <input id={`${id}-number`} className={field} value={f.number} maxLength={60} onChange={set('number')} />
      </div>
      <ReasonFields reason={reason} text={text} setReason={setReason} setText={setText} idPrefix={id} />
      {correct.error && <Refusal error={correct.error} />}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={!ready || correct.isPending}>{correct.isPending ? 'Saving…' : 'Save the correction'}</button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}

function WithdrawBond({ clientId, bond, onDone }: { clientId: string; bond: Bond; onDone: (m: string | null) => void }) {
  const [why, setWhy] = useState('');
  const withdraw = useWithdrawBond(clientId);
  const id = `bondw-${bond.id}`;
  return (
    <form className="mt-2 space-y-3 rounded-lg border border-neutral-300 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!why.trim()) return;
            withdraw.mutate({ bondId: bond.id, reason: why.trim() },
              { onSuccess: () => onDone('Bond withdrawn. Whatever it completed is open again.') });
          }}>
      <h3 className="font-semibold text-neutral-900">Withdraw the bond</h3>
      <p className={hint}>For a bond entered in error. It stays on the record, counts for nothing, and the deadline it completed reopens.</p>
      <div>
        <label className={label} htmlFor={`${id}-why`}>Why</label>
        <input id={`${id}-why`} className={field} value={why} maxLength={2000} onChange={(e) => setWhy(e.target.value)} />
      </div>
      {withdraw.error && <Refusal error={withdraw.error} />}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={!why.trim() || withdraw.isPending}>Withdraw</button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}

/** A Done task's bond, read only, as the Legal section shows it (A-#205's shape for the closed task's facts). */
export function BondFacts({ clientId, orderId }: { clientId: string; orderId: string }) {
  const q = useClientBonds(clientId);
  if (!q.data) return null;
  const order = q.data.orders.find((o) => o.id === orderId);
  const bonds = q.data.bonds.filter((b) => b.court_order_id === orderId && !b.withdrawn);
  if (!order) return null;
  return (
    <div className="mt-2 text-neutral-900">
      <p className="font-semibold">{orderWords(order)}</p>
      <p className="text-sm text-neutral-700">{procuredSaid(order, formatDate)}</p>
      <ul className="mt-1 text-sm">
        {bonds.map((b) => (
          <li key={b.id}>{b.increases_bond_id ? 'Rider' : 'Bond'}{b.amount ? ` · $${b.amount}` : ''} · {bondState(b, formatDate)}
            {b.surety_name ? ` · ${b.surety_name}` : ''}</li>
        ))}
      </ul>
    </div>
  );
}
